import { PayloadAction, createAction, createSlice } from '@reduxjs/toolkit'
import { eventChannel } from 'redux-saga'
import { all, call, cancelled, put, take, takeEvery } from 'redux-saga/effects'

import * as schema from '../schema'
import { FireEvent, getDatabase } from '../api'
import { DeepReadonly, log, processErr } from '../utils'
import { YieldReturn } from './common'

const PREFIX = 'closers'

type ClosersState = DeepReadonly<{
  closers: schema.CloserOrSetter[]
}>

const initialState: ClosersState = {
  closers: [],
}

type ClosersChannelPayload = FireEvent<schema.CloserOrSetter>[]

const closersSlice = createSlice({
  name: PREFIX,
  initialState,
  reducers: {
    closersAction() {
      // TODO: ??
      // state.closers
    },
    receivedClosers(
      state,
      {
        payload: { closers },
      }: PayloadAction<{ closers: schema.CloserOrSetter[] }>,
    ) {
      state.closers = closers
    },
  },
})

export const {
  actions: { closersAction, receivedClosers },
  reducer: closers,
} = closersSlice

export const addCloser = createAction<schema.CloserOrSetter>(
  `${PREFIX}/addCloser`,
)
export const removeCloser = createAction<schema.CloserOrSetter>(
  `${PREFIX}/removeCloser`,
)
export const updateCloser = createAction<{
  old: schema.CloserOrSetter
  updated: schema.CloserOrSetter
}>(`${PREFIX}/updateCloser`)

export type CloserOpResult = { readonly ok: boolean; readonly err?: string }
export const closerOpResult = createAction<CloserOpResult>(
  `${PREFIX}/closerOpResult`,
)

//#region selectors

interface GlobalState {
  [PREFIX]: ClosersState
}

export type SelectClosers = (state: GlobalState) => schema.CloserOrSetter[]

export const selectClosers =
  () =>
  ({ closers: { closers } }: GlobalState): schema.CloserOrSetter[] =>
    closers as schema.CloserOrSetter[]

//#endregion selectors

const createClosersChannel = () =>
  eventChannel<ClosersChannelPayload | Error>((emit) => {
    const dbRef = getDatabase().ref(`Closers`)

    dbRef.on('value', (data) => {
      emit(data.val() as ClosersChannelPayload)
    })

    return () => {
      dbRef.off()
    }
  })

function* closersWatcher() {
  const closersChannel: YieldReturn<typeof createClosersChannel> = yield call(
    createClosersChannel,
  )
  try {
    while (true) {
      const payload: ClosersChannelPayload = yield take(closersChannel)
      yield put(
        receivedClosers({
          closers: payload as unknown as schema.CloserOrSetter[],
        }),
      )
    }
  } finally {
    log(`closers sub finally()`)

    if ((yield cancelled()) as boolean) {
      log('closers sub finally() -> cancelled() , will close channel.')
      closersChannel.close()
    }
  }
}

function* addCloserFn({ payload }: ReturnType<typeof addCloser>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Closers').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() => db.ref('Closers').set([...current, payload]))
    yield put(closerOpResult({ ok: true }))
  } catch (e) {
    log(`${addCloserFn.name}*() -> ${processErr(e)}`)
    yield put(closerOpResult({ ok: false, err: processErr(e) }))
  }
}

function* removeCloserFn({ payload }: ReturnType<typeof removeCloser>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Closers').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() =>
      db.ref('Closers').set(current.filter((c) => c.value !== payload.value)),
    )
    yield put(closerOpResult({ ok: true }))
  } catch (e) {
    log(`${removeCloserFn.name}*() -> ${processErr(e)}`)
    yield put(closerOpResult({ ok: false, err: processErr(e) }))
  }
}

function* updateCloserFn({
  payload: { old, updated },
}: ReturnType<typeof updateCloser>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Closers').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() =>
      db
        .ref('Closers')
        .set(current.map((c) => (c.value === old.value ? updated : c))),
    )
    yield put(closerOpResult({ ok: true }))
  } catch (e) {
    log(`${updateCloserFn.name}*() -> ${processErr(e)}`)
    yield put(closerOpResult({ ok: false, err: processErr(e) }))
  }
}

export function* closersSaga() {
  yield all([
    takeEvery(closersAction, closersWatcher),
    takeEvery(addCloser, addCloserFn),
    takeEvery(removeCloser, removeCloserFn),
    takeEvery(updateCloser, updateCloserFn),
  ])
}
